import { DialogDescription } from '@radix-ui/react-dialog'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import {
  type SubmitHandler,
  useForm,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useModifyNAVItems } from 'components/hooks/needs_attention/useModifyNAVItems'
import { Banner } from 'components/reusable/Banner'
import { FormDatePickerField } from 'components/reusable/form/FormFields'
import { Button } from 'components/ui/button'
import {
  convertToUTCDate,
  getDateNumDaysFromStartDate,
  getDateString,
} from 'utils'

import NoteEditor from './NoteEditor'
import { setMissingPaymentsQueryDataCallback } from './utils'
import NeedsAttentionContext from '../../context/NeedsAttentionContext'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog'

export type SilenceFormInput = {
  content: string
  silence_until_date: string
}

export function SilenceDialog ({
  open,
  missingPaymentId,
  setOpen,
  children,
}: {
  open: boolean
  missingPaymentId: string
  setOpen: (open: boolean) => void
  children: React.ReactNode
}) {
  const queryClient = useQueryClient()
  const { getBrokersMissingPaymentsQueryKey } = React.useContext(
    NeedsAttentionContext
  )
  const [error, setError] = useState<GrapeAPIErrorType | undefined>()

  const { handleSubmit, setValue, watch, reset } = useForm<SilenceFormInput>({
    defaultValues: {
      content: '',
      silence_until_date: '',
    },
  })

  const silenceNAVItemsMutation = useModifyNAVItems({
    ids: [missingPaymentId],
  })

  const onSubmit: SubmitHandler<SilenceFormInput> = async (data) => {
    silenceNAVItemsMutation.mutate(
      { ...data },
      {
        onSuccess: (response) => {
          queryClient.setQueryData(
            getBrokersMissingPaymentsQueryKey,
            setMissingPaymentsQueryDataCallback(response)
          )
          setOpen(false)
          reset()
        },
        onError: (response) => {
          setError(response)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className='text-lg'>Silence</DialogTitle>
        </DialogHeader>
        <DialogHeader>
          <DialogDescription className='text-sm mb-2'>
            Please add a note before silencing! Any content written in the field
            below will be submitted as a note when silencing.
          </DialogDescription>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            <SilenceNAVItemsForm
              setValue={setValue}
              error={error}
              watch={watch}
              isPending={silenceNAVItemsMutation.isPending}
            />
            <div className='flex justify-center gap-4'>
              <Button>Silence</Button>
              <Button
                variant='secondary'
                onClick={() => {
                  setOpen(false)
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}

type SilenceNAVItemsFormProps = {
  error?: GrapeAPIErrorType | undefined
  setValue: UseFormSetValue<SilenceFormInput>
  watch: UseFormWatch<SilenceFormInput>
  isPending: boolean
  displayNoteEditor?: boolean
}

export const SilenceNAVItemsForm = ({
  setValue,
  error,
  watch,
  isPending,
  displayNoteEditor = true,
}: SilenceNAVItemsFormProps) => {
  return (
    <>
      {error && (
        <Banner type='critical' titleText={error.message}>
          {error.errors?.map((error) => (
            <div key='error'>{error}</div>
          ))}
        </Banner>
      )}
      {displayNoteEditor && <NoteEditor setValue={(value) => setValue('content', value)} />}
      <FormDatePickerField
        fieldId='silence_until_date'
        fieldLabel='Silence until'
        watch={watch}
        disabled={isPending}
        selectedDateConversionFn={(selectedDate) =>
          selectedDate ? convertToUTCDate(new Date(selectedDate)) : new Date()}
        handleSelect={(date: Date | undefined) => {
          // Convert date to string so the timezone doesn't affect how its passed to the API.
          if (date) {
            setValue('silence_until_date', getDateString(date), {
              shouldValidate: true,
            })
          }
        }}
      />
      <div className='flex item-center justify-between flex-grow'>
        <Button
          type='button'
          variant='outline'
          className='w-[31%]'
          data-testid='one-day'
          onClick={() =>
            setValue(
              'silence_until_date',
              getDateString(
                getDateNumDaysFromStartDate(getDateString(new Date()), '1')
              )
            )}
        >
          1 day
        </Button>
        <Button
          type='button'
          variant='outline'
          className='w-[31%]'
          data-testid='three-days'
          onClick={() =>
            setValue(
              'silence_until_date',
              getDateString(
                getDateNumDaysFromStartDate(getDateString(new Date()), '3')
              )
            )}
        >
          3 days
        </Button>
        <Button
          type='button'
          variant='outline'
          className='w-[31%]'
          data-testid='seven-days'
          onClick={() =>
            setValue(
              'silence_until_date',
              getDateString(
                getDateNumDaysFromStartDate(getDateString(new Date()), '7')
              )
            )}
        >
          7 days
        </Button>
      </div>
    </>
  )
}
